import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VehicleChallengeGate from '../components/Portal/VehicleChallengeGate';
import { clearChallengeToken } from '../services/portalChallenge';
import { io, Socket } from 'socket.io-client';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Divider,
  Table,
  Tag,
  Spin,
  Result,
  Modal,
  Input,
  message,
  Alert,
  Empty,
  DatePicker,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import { publicApi } from '../services/apiInstances';
import { formatMeasurement } from '../components/DVI/notePresets';
import CustomerLineNote from '../components/Portal/CustomerLineNote';
import MissingInfoPrompt, {
  CaptureFieldSpec,
  MissingFieldSpec,
} from '../components/Portal/MissingInfoPrompt';
import EstimatePaymentPanel from '../components/Portal/EstimatePaymentPanel';
import PostApprovalRegistration from '../components/Portal/PostApprovalRegistration';
import ApprovalAccountSetup, {
  accountSetupProblem,
} from '../components/Portal/ApprovalAccountSetup';
import dayjs, { Dayjs } from 'dayjs';
import {
  calculateEstimateTotals,
  feesFromStored,
  FeesForTotals,
  WholeJobDiscountInput,
} from '../utils/estimateTotals';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Socket origin for the live-update channel. Derived from REACT_APP_API_URL the
// same way services/socketService.ts does; if it's unset we simply skip the live
// socket (the page still works, just without on-the-fly refresh).
const SOCKET_URL = (process.env.REACT_APP_API_URL || '').replace('/api', '');

/**
 * Reconstruct the estimate's fee split (taxable / non-taxable) from its STORED
 * money fields. The public GET payload carries the fee-inclusive stored subtotal /
 * discount / tax / total but not the individual fee rows, so we back it out:
 *   feesTotal   = total − (subtotal − discount) − tax           (feesFromStored)
 *   taxableFees = (storedTaxBase) − (line-only taxable base)
 * where storedTaxBase = stored tax ÷ rate and the line-only base is what the SAME
 * display helper computes for the full estimate. This keeps the public approval
 * page's Tax/Total on the fee-inclusive amount the customer is actually billed —
 * without a fee row the page showed a total $10.70 under the bill.
 */
export function deriveEstimateFees(
  estimate: {
    subtotal?: number | null;
    discount_amount?: number | null;
    tax_amount?: number | null;
    total_amount?: number | null;
    tax_rate?: number | null;
    line_items?: any[];
  },
  wholeJobDiscount?: WholeJobDiscountInput | null
): FeesForTotals {
  const feesTotal = feesFromStored(estimate);
  if (feesTotal <= 0) return { taxable: 0, nonTaxable: 0 };

  const rate = Number(estimate.tax_rate) || 0;
  if (rate <= 0) {
    // No sales tax at all → the taxable/non-taxable split can't move the total.
    return { taxable: 0, nonTaxable: feesTotal };
  }

  // Tax base implied by the stored (fee-inclusive) tax amount.
  const storedTaxBase = (Number(estimate.tax_amount) || 0) / (rate / 100);
  // Taxable base contributed by the LINES alone — recomputed with the very helper
  // the page displays. It MUST be scoped to the same lines the STORED totals were
  // built from: after a partial approval the stored subtotal/tax are APPROVED-scope
  // (declined lines dropped). Deriving over the full line set inflates lineTaxBase
  // past storedTaxBase, driving the taxable-fee split negative → clamped to 0 → a
  // taxable fee mislabeled non-taxable and the tax-on-fee silently dropped. Scope is
  // taken from each line's stored approval_status so it always matches the stored
  // totals; a not-yet-approved estimate hydrates to all-unselected → full scope.
  const storedScope = hydrateLineItemStatus(estimate.line_items || []);
  const lineOnly = calculateEstimateTotals(estimate.line_items || [], rate, storedScope, wholeJobDiscount);
  const lineTaxBase = lineOnly.taxAmount / (rate / 100);

  let taxableFees = round2(storedTaxBase - lineTaxBase);
  taxableFees = Math.max(0, Math.min(taxableFees, feesTotal));
  const nonTaxableFees = round2(feesTotal - taxableFees);
  return { taxable: taxableFees, nonTaxable: nonTaxableFees };
}

/**
 * Map each line's STORED approval_status to the page's selection state so a reload
 * of an already-approved estimate reflects the approved SCOPE (approved lines only)
 * instead of resetting every line to 'unselected' and re-showing the full total.
 */
export function hydrateLineItemStatus(
  lineItems: Array<{ id: number; approval_status?: string | null }>
): Record<number, 'approved' | 'declined' | 'unselected'> {
  const out: Record<number, 'approved' | 'declined' | 'unselected'> = {};
  for (const li of lineItems || []) {
    out[li.id] =
      li.approval_status === 'approved'
        ? 'approved'
        : li.approval_status === 'declined'
        ? 'declined'
        : 'unselected';
  }
  return out;
}

type LineSelection = 'approved' | 'declined' | 'unselected';

/**
 * Merge a fresh server-derived selection map with the customer's current one for
 * a LIVE (socket-triggered) refetch. The customer's un-submitted local choices
 * (lines in `locallyChanged`) win; every other line takes the server value — and
 * any line the staff just ADDED (present in server, absent from prev) shows up.
 * This is what preserves in-progress selections when staff edit an estimate the
 * customer is actively viewing.
 */
export function mergeLineItemStatus(
  prev: Record<number, LineSelection>,
  serverStatus: Record<number, LineSelection>,
  locallyChanged: Set<number>
): Record<number, LineSelection> {
  const merged: Record<number, LineSelection> = { ...serverStatus };
  locallyChanged.forEach(lineId => {
    if (prev[lineId] !== undefined) merged[lineId] = prev[lineId];
  });
  return merged;
}

interface Estimate {
  id: number;
  estimate_number: string;
  status: string;
  /**
   * WP-10 slice 5 — the customer this document belongs to. Sent to
   * registration so a new account CLAIMS the existing record rather than
   * creating a duplicate customer beside it. Optional because the payload has
   * always carried it implicitly (the route spreads the estimate row) and
   * older responses should not become a type error at the seam.
   */
  customer_id?: number;
  customer_first_name: string;
  customer_last_name: string;
  customer_company_name?: string;
  customer_phone: string;
  customer_email: string;
  year: number;
  make: string;
  model: string;
  vin: string;
  license_plate: string;
  customer_notes?: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  valid_until?: string;
  disclaimer?: string;
  line_items: any[];
  signed_at?: string;
  signature_data?: string;
  signature_type?: 'remote' | 'in_person';
  inspection?: DVIInspectionPayload | null;
}

interface Part {
  id: number;
  part_number: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percentage?: number;
  discount_amount?: number;
  discounted_price?: number;
}

interface DVIPhoto {
  file_url: string;
  notes: string | null;
}

interface DVIItem {
  id: number;
  point_name: string;
  category_name: string;
  status: string;
  notes: string | null;
  photos: DVIPhoto[];
  point_type?: string;
  unit?: string | null;
  reading_value?: string | null;
  /**
   * WP-07 — the shop-configured label for this measurement ("Wear",
   * "Wear Amount", "Damage Location"). Without it the customer sees a bare
   * "2mm" and cannot tell what was measured.
   */
  measurement_label?: string | null;
}

interface DVIInspectionPayload {
  summary_notes: string | null;
  items: DVIItem[];
}

/**
 * `approval_review` on the public estimate payload (Plan 2 Task 7).
 *
 * Labels, never column names: `note` is staff prose but `field` is a raw column,
 * and "current_mileage" in a correction prompt is how the prompt becomes
 * unreadable — the server supplies `label` for exactly that reason.
 */
interface ApprovalReviewInfo {
  state: string;
  return_note: string | null;
  returned_fields: Array<{ field: string; label: string; note: string | null }>;
}

const PublicEstimateView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const isInPerson = searchParams.get('inperson') === 'true';
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // WP-04: the document is locked and this browser has not proved anything yet.
  // Distinct from `error` on purpose — this is not a failure, it is a prompt.
  const [needsChallenge, setNeedsChallenge] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [signatureRef, setSignatureRef] = useState<SignatureCanvas | null>(null);
  const [inPersonSignatureRef, setInPersonSignatureRef] = useState<SignatureCanvas | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lineItemStatus, setLineItemStatus] = useState<{ [key: number]: 'approved' | 'declined' | 'unselected' }>({});
  /**
   * WP-10 slice 2 — notes the customer has typed against individual lines,
   * keyed by line id. Only lines they actually wrote on appear here; the map is
   * sent alongside the approval so a note and the decision it explains arrive
   * together. A note is also accepted on its own via the dedicated endpoint,
   * for the customer who wants to ask a question without committing to
   * anything — see handleLineNoteCommit.
   */
  const [lineNotes, setLineNotes] = useState<{ [key: number]: string | null }>({});
  /**
   * WP-10 slice 3 — the customer details still missing before this job can be
   * started, and what the customer has typed into the prompt.
   *
   * `missingFields` is whatever the SERVER reported in `completeness.missing`.
   * It is never computed here: WP-02 owns the rules, and a second copy in the
   * client would drift silently in the worst direction — prompting for
   * something the API refuses, or not prompting for something that blocks the
   * approval.
   */
  const [missingFields, setMissingFields] = useState<CaptureFieldSpec[]>([]);
  /**
   * Account setup, offered alongside the details the customer is already typing.
   *
   * Owner ask 2026-07-30: the offer used to appear only after `status === 'approved'`,
   * so the customer filled in name/email/phone to approve and was then handed a
   * second form asking for the same things again plus a password. Only the password
   * is asked here; the registration reuses the values from the prompt above and the
   * portal token that authorized the approval.
   *
   * The password is held in component state and never banked into the server-side
   * registration draft — see PostApprovalRegistration, which has the same rule.
   */
  const [accountOptIn, setAccountOptIn] = useState(false);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountConfirm, setAccountConfirm] = useState('');
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [customerFieldValues, setCustomerFieldValues] = useState<Record<string, string>>({});
  const [customerFieldErrors, setCustomerFieldErrors] = useState<Record<string, string>>({});
  const [captureBlocked, setCaptureBlocked] = useState(false);
  /**
   * The server's own verdict on whether this record can approve
   * (`completeness.complete`).
   *
   * `null` means the payload carried no verdict at all — an older API, or the
   * completeness lookup threw and the route logged it and carried on. That is
   * deliberately treated as "no gate": failing OPEN here is right because the
   * server refuses the approval itself. A page that blocks because it could not
   * read a policy would stop the shop taking approvals for a reason nobody in
   * the shop can see.
   */
  const [completenessComplete, setCompletenessComplete] = useState<boolean | null>(null);
  /**
   * Plan 2 Task 7 — set when this approval was sent back to the customer:
   * `{ state, return_note, returned_fields: [{ field, label, note }] }`, or null
   * for every estimate that has never been returned (which is nearly all of
   * them, hence null rather than an empty object — the page must not grow an
   * empty "issues" panel for the normal case).
   */
  const [approvalReview, setApprovalReview] = useState<ApprovalReviewInfo | null>(null);
  /** Scroll target for a server-side refusal — see handleSignatureSubmit. */
  const capturePromptRef = useRef<HTMLDivElement | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Appointment booking state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [canSchedule, setCanSchedule] = useState<any>(null);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingProcessing, setBookingProcessing] = useState(false);
  const [existingAppointment, setExistingAppointment] = useState<any>(null);
  const [bookingSettings, setBookingSettings] = useState<any>(null);
  const [previewPhoto, setPreviewPhoto] = useState<DVIPhoto | null>(null);

  // ── The approval gate, as the customer experiences it ───────────────────────
  //
  // Plan 3 Task 4. Nothing here decides WHAT is required — that is
  // `completeness` from the server. What it decides is whether the customer can
  // usefully press Approve yet, so they are told before a round trip instead of
  // meeting a 409 they had no way to predict.

  /**
   * What is actually in the box: what the customer typed if they typed anything,
   * otherwise the value already on file. `??` not `||`, so a field the customer
   * deliberately cleared reads as cleared.
   */
  const gatedFieldValue = (m: CaptureFieldSpec): string =>
    String(customerFieldValues[m.field] ?? m.value ?? '').trim();

  /** Required fields that are still blank. Suggested fields never appear here. */
  const blankRequiredFields = missingFields.filter(
    (m) => m.required !== false && !gatedFieldValue(m)
  );

  /**
   * What the account would be keyed on: whatever the customer has typed into the
   * prompt, otherwise what the shop already holds. Registration accepts an email OR
   * a phone, and the offer is only usable when one of them exists — asking for a
   * password with neither would create an account nobody could sign in to.
   *
   * `.trim() || undefined` rather than `??`: an empty string is not an identifier,
   * and the register endpoint would reject it as a malformed email.
   */
  /**
   * A diagnosis the customer already paid for, credited against this quote
   * (migration 191). Stored in CENTS on the estimate and applied POST-TAX, so it
   * is displayed as its own line under the tax rather than folded into the
   * subtotal.
   */
  const diagnosisCredit = round2((Number((estimate as any)?.diagnosis_credit_cents) || 0) / 100);

  const accountEmail =
    (customerFieldValues.email ?? estimate?.customer_email ?? '').trim() || null;
  const accountPhone =
    (customerFieldValues.phone ?? estimate?.customer_phone ?? '').trim() || null;
  const accountIdentifier = accountEmail || accountPhone;

  /**
   * Blank, or flagged by a submission the server just refused. The second half
   * matters: after a 409 the value may be present and simply wrong, and a button
   * that re-enables the instant the server said no invites the same refusal
   * again. Editing the field clears its error, which lifts the gate.
   */
  const outstandingRequiredFields = missingFields.filter(
    (m) => m.required !== false && (!gatedFieldValue(m) || !!customerFieldErrors[m.field])
  );

  /**
   * Approve is disabled only when BOTH halves hold: the server says this record
   * cannot approve, AND there is something on this screen the customer can do
   * about it.
   *
   * Both halves are load-bearing. Without the first, the page is inventing
   * policy. Without the second, a field the customer cannot fix here (a
   * staff-only field, or the `preferred_contact_channel` pseudo-field) would
   * produce a permanently dead button with no route past it — strictly worse
   * than letting the press through and showing the server's answer.
   */
  const approvalGated = completenessComplete === false && outstandingRequiredFields.length > 0;

  /**
   * Why the button is off. A disabled control with no explanation is
   * indistinguishable from a broken page, and the customer's only recourse is to
   * phone the shop — which is the cost this whole feature exists to avoid.
   *
   * Fields are named by the server's LABEL. A column name reaching a customer
   * ("current_mileage") is unreadable and leaks the schema for nothing.
   */
  const approveBlockedReason = `Approve becomes available once ${outstandingRequiredFields
    .map((m) => m.label)
    .join(', ')} ${outstandingRequiredFields.length === 1 ? 'is' : 'are'} filled in above. This protects both you and the shop.`;

  /**
   * What actually goes on the wire with the approval: only values the customer
   * CHANGED from what the server sent down.
   *
   * Found by driving the real app. `applyPublicCustomerFields` is fill-only — a
   * publicly-writable field that already has a value answers
   * `422 FIELD_NOT_CAPTURABLE` and applies NOTHING, aborting the whole approval.
   * Before the prompt was prefilled that could not happen, because the customer
   * only ever typed into blanks. Prefilled, merely touching an address box (or
   * retrying after a refusal that half-applied) sent a value already on file and
   * made the estimate unapprovable with no way past it on screen.
   *
   * Trimmed comparison, because a trailing space the customer did not intend is
   * not a correction, and the server trims before storing anyway.
   */
  const changedCustomerFields = (): Record<string, string> => {
    const spec = new Map(missingFields.map((m) => [m.field, m]));
    const out: Record<string, string> = {};
    for (const [field, typed] of Object.entries(customerFieldValues)) {
      const stored = spec.get(field)?.value ?? '';
      if (String(typed).trim() === String(stored ?? '').trim()) continue;
      out[field] = typed;
    }
    return out;
  };

  /**
   * Staff notes from a returned approval, keyed by field so
   * `MissingInfoPrompt`'s existing `fieldErrors` renders each one against the
   * input it is about. Kept separate from `customerFieldErrors` on purpose: a
   * returned note is standing guidance, not a fresh validation failure, so it
   * must not gate the approve button and must survive a reload.
   */
  const returnedFieldNotes: Record<string, string> = {};
  for (const f of approvalReview?.returned_fields ?? []) {
    if (f.note) returnedFieldNotes[f.field] = f.note;
  }

  // ── Live-update plumbing ────────────────────────────────────────────────────
  // Dedicated public socket (kept separate from the staff NotificationContext
  // socket so it never clobbers it).
  const socketRef = useRef<Socket | null>(null);
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Line ids the customer has toggled locally but NOT yet submitted. A live
  // refetch must keep these choices instead of overwriting them with the server's
  // approval_status, so an in-flight staff edit never resets the customer's picks.
  const locallyChangedRef = useRef<Set<number>>(new Set());
  // True while the customer is mid-interaction (signing, declining, booking, or a
  // request is processing). A live update arriving now is deferred, not applied,
  // so we never yank the UI mid-tap. Recomputed every render.
  const busyRef = useRef(false);
  busyRef.current =
    processing || showSignatureModal || showDeclineModal || showBookingModal;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (token) {
      fetchEstimate();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live updates: once the estimate is loaded, open a dedicated public socket
  // authenticated by the estimate's public token, join ONLY this estimate's room,
  // and refetch-with-merge whenever staff edits it. The customer's in-progress
  // selections and signature state are preserved (see fetchEstimate merge path).
  useEffect(() => {
    const estimateId = estimate?.id;
    if (!estimateId || !token || !SOCKET_URL) return;

    const socket = io(SOCKET_URL, {
      auth: { publicToken: token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    const joinRoom = () => socket.emit('join', `estimate:${estimateId}`);
    socket.on('connect', joinRoom);

    const handleEntityUpdate = () => {
      const activeEl = document.activeElement;
      const isTypingInline =
        !!activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA');
      // Defer if the customer is mid-interaction so we never yank the UI.
      if (busyRef.current || isTypingInline) {
        if (pendingUpdateRef.current) clearTimeout(pendingUpdateRef.current);
        pendingUpdateRef.current = setTimeout(() => {
          if (!busyRef.current) {
            fetchEstimate(true);
            message.info({ content: 'This estimate was just updated', duration: 2 });
          }
        }, 2000);
        return;
      }
      fetchEstimate(true);
      message.info({ content: 'This estimate was just updated', duration: 2 });
    };
    socket.on('entity_updated', handleEntityUpdate);

    return () => {
      socket.off('connect', joinRoom);
      socket.off('entity_updated', handleEntityUpdate);
      socket.disconnect();
      socketRef.current = null;
      if (pendingUpdateRef.current) {
        clearTimeout(pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }
    };
  }, [estimate?.id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to booking section when estimate is approved
  useEffect(() => {
    if (estimate?.status === 'approved' && estimate?.signed_at && !existingAppointment) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 500);
    }
  }, [estimate?.status, estimate?.signed_at, existingAppointment]);

  // preserveLocal=true → a background (socket-triggered) refetch: don't flash the
  // full-page spinner, don't blow the page away on a transient error, and MERGE
  // the customer's in-progress selections instead of overwriting them.
  const fetchEstimate = async (preserveLocal = false) => {
    try {
      if (!preserveLocal) setLoading(true);
      const response = await publicApi.get(`/public/estimates/${token}`);
      const estimateData = response.data;
      setEstimate(estimateData);

      // WP-10 slice 3 — what the customer still has to supply, computed by
      // WP-02 on the server and returned in `completeness`. This payload has
      // carried the list since WP-02 shipped; nothing rendered it, so the
      // capture path was decided but unreachable.
      //
      // Read straight from the response with no local rule of its own. The
      // server has already narrowed the list to fields a public token may write
      // (the postal address block), so anything here is safe to prompt for and
      // will be accepted on submit.
      // Prefer `completeness.fields` (2026-07-29): EVERY gated field with its
      // current value, so the customer verifies as well as fills — a misspelt name
      // or the wrong vehicle on the quote is only caught if it is on screen.
      // `completeness.missing` is the older blanks-only list and stays the
      // fallback for any payload that has not been updated.
      const serverMissing: CaptureFieldSpec[] = Array.isArray(estimateData?.completeness?.fields)
        ? estimateData.completeness.fields
        : Array.isArray(estimateData?.completeness?.missing)
          ? estimateData.completeness.missing
          : [];
      setMissingFields(serverMissing);
      // The server's verdict, not a re-derivation of it. Anything that is not a
      // boolean means the payload carried no verdict, which is treated as "no
      // gate" — see the `completenessComplete` declaration.
      setCompletenessComplete(
        typeof estimateData?.completeness?.complete === 'boolean'
          ? estimateData.completeness.complete
          : null
      );
      // Did this approval come back to them, and which fields? Null for every
      // estimate that has never been returned, so nothing renders.
      setApprovalReview(
        estimateData?.approval_review && estimateData.approval_review.state
          ? {
              state: String(estimateData.approval_review.state),
              return_note: estimateData.approval_review.return_note ?? null,
              returned_fields: Array.isArray(estimateData.approval_review.returned_fields)
                ? estimateData.approval_review.returned_fields
                : [],
            }
          : null
      );
      // Stale errors from a previous rejected submit must not survive a reload
      // that may well have fixed them.
      setCustomerFieldErrors({});
      setCaptureBlocked(false);

      // Hydrate existing appointment from the payload so the "Book Your
      // Appointment" CTA stays suppressed across reloads. The Estimate type does
      // not declare an appointment field, so probe the common shapes the backend
      // might return. NOTE: if the GET /public/estimates/:token response does not
      // actually include any of these fields, this stays a no-op and a backend
      // field is required to fully fix the reload-CTA issue.
      const existingAppt =
        estimateData?.appointment ||
        estimateData?.existing_appointment ||
        estimateData?.scheduled_appointment;
      if (existingAppt) {
        setExistingAppointment(existingAppt);
      }

      // Expand all rows by default
      if (estimateData.line_items) {
        const allKeys = estimateData.line_items.map((item: any) => item.id);
        setExpandedRowKeys(allKeys);

        // Hydrate selection from each line's STORED approval_status so a reload of
        // an already-approved estimate shows the approved SCOPE. Truly-undecided
        // lines (pending / null) default to 'unselected' — the pre-approval flow.
        const serverStatus = hydrateLineItemStatus(estimateData.line_items);
        if (preserveLocal) {
          // Live merge: keep the customer's un-submitted local choices; take the
          // server value only for lines the customer hasn't touched (and for any
          // lines the staff just added).
          setLineItemStatus(prev =>
            mergeLineItemStatus(prev, serverStatus, locallyChangedRef.current)
          );
        } else {
          // Full (initial / post-submit) load: the server is authoritative and any
          // prior local choices are already persisted, so reset the dirty set.
          locallyChangedRef.current = new Set();
          setLineItemStatus(serverStatus);
        }
      }
    } catch (err: any) {
      console.error('Error fetching estimate:', err);
      // WP-04: the document is locked and this browser has not proved anything
      // yet. Show the vehicle challenge instead of "not found" — the customer
      // has a perfectly good link, they just have not been let in.
      //
      // Keyed on the machine-readable `code`, not the message, so rewording the
      // refusal cannot silently turn the gate back into a 404 page.
      if (err?.response?.status === 401 && err?.response?.data?.code === 'PORTAL_ACCESS_REQUIRED') {
        // A stored session that no longer works (expired, revoked, or from a
        // previous document) must be dropped, or the retry loops forever
        // re-sending a credential the server has already refused.
        clearChallengeToken(token);
        if (!preserveLocal) setNeedsChallenge(true);
        return;
      }
      // A background merge must never replace the live page with the error state.
      if (!preserveLocal) {
        setError(err.response?.data?.error || 'Failed to load estimate');
      }
    } finally {
      if (!preserveLocal) setLoading(false);
    }
  };

  /**
   * WP-10 slice 2 — a note committed against one line (on blur).
   *
   * Held in local state so it rides up with the approval, AND written straight
   * to the server. The second half is the point: a customer who types a
   * question and then closes the tab without approving has still asked the
   * question, and the shop can answer it. Making the note conditional on
   * approving would mean the only way to raise a concern is to accept the work
   * you are concerned about.
   *
   * The write is best-effort and deliberately silent on failure. This fires on
   * blur, which happens as the customer moves between fields; an error toast
   * there interrupts someone mid-form for a note that will be retried with the
   * approval anyway.
   */
  const handleLineNoteCommit = async (lineId: number, note: string | null) => {
    setLineNotes(prev => ({ ...prev, [lineId]: note }));
    try {
      await publicApi.post(`/public/estimates/${token}/lines/${lineId}/note`, { note });
    } catch (err) {
      console.error('Error saving line note:', err);
    }
  };

  const handleApprove = () => {
    setShowSignatureModal(true);
  };

  const handleSignatureSubmit = async () => {
    const activeRef = isInPerson ? inPersonSignatureRef : signatureRef;
    if (!activeRef || activeRef.isEmpty()) {
      message.warning('Please provide your signature');
      return;
    }

    // Check if at least one item is approved
    const hasApprovedItems = Object.values(lineItemStatus).some(status => status === 'approved');
    if (!hasApprovedItems) {
      message.warning('Please approve at least one line item');
      return;
    }

    // WP-10 slice 3 — the missing details must be filled in before the approval
    // is sent.
    //
    // Checked here rather than left to the server's 422 purely so the customer
    // is told before a round trip, not instead of it: the server re-derives the
    // list from its own rules and rejects the whole approval if anything is
    // still missing. This is a courtesy, never the control.
    //
    // Two things this must NOT do, both of which it used to:
    //  - count a prefilled field as blank. The prompt now arrives filled from the
    //    record (2026-07-29), so a check that only looked at what the customer
    //    TYPED saw every field empty and refused a complete customer's approval
    //    outright.
    //  - count a `suggested` field. `completeness.fields` carries them too; they
    //    are asked for and never block, which is the entire difference between
    //    the two levels.
    if (blankRequiredFields.length > 0) {
      setCaptureBlocked(true);
      setCustomerFieldErrors(
        blankRequiredFields.reduce((acc: Record<string, string>, m) => {
          acc[m.field] = 'Required before we can start the work';
          return acc;
        }, {})
      );
      message.warning('Please fill in the details we still need');
      return;
    }

    // The account offer is optional, but an opted-in setup that cannot succeed has
    // to be caught BEFORE the approval: approving first and failing the registration
    // afterwards would leave the customer with an approved job, a typo'd password
    // they cannot see, and no account. Same rule the component renders.
    const setupProblem = accountSetupProblem({
      enabled: accountOptIn && !accountCreated,
      password: accountPassword,
      confirm: accountConfirm,
      identifier: accountIdentifier,
    });
    if (setupProblem) {
      setAccountError(setupProblem);
      setShowSignatureModal(false);
      message.warning(setupProblem);
      return;
    }

    try {
      setProcessing(true);
      const signatureData = activeRef.toDataURL();

      await publicApi.post(`/public/estimates/${token}/approve`, {
        signature: signatureData,
        lineItemStatus: lineItemStatus,
        signature_type: isInPerson ? 'in_person' : 'remote',
        // WP-10 slice 2. Sent even though each note was already written on
        // blur: the last box the customer typed in may still be focused when
        // they hit Approve, so its blur has not fired. Re-sending the whole map
        // makes the approval the point at which every note is definitely
        // recorded. Writing the same text twice is a no-op.
        lineNotes,
        // WP-10 slice 3 — the details the customer just supplied, sent with the
        // approval rather than as a separate save. The server applies them
        // BEFORE committing the approval and rejects the whole request if they
        // are bad, so the customer sees one clear failure instead of an
        // approved estimate that silently dropped their address.
        //
        // Only fields the server itself listed as missing are sent; it refuses
        // anything else, and it is fill-only, so this can never overwrite an
        // address staff already verified.
        //
        // Only DIFFERENCES, too — see changedCustomerFields. Re-sending a value
        // already on file is a 422 that aborts the entire approval.
        customerFields: Object.keys(changedCustomerFields()).length > 0
          ? changedCustomerFields()
          : undefined,
      });

      message.success('Estimate approved successfully! Redirecting...', 3);
      setShowSignatureModal(false);

      // The account, if they asked for one. AFTER the approval, deliberately: the
      // approval is what the customer came to do, and it also applies the details
      // this registration relies on. A failure here is reported on its own and never
      // presented as the approval having failed — it already succeeded.
      if (accountOptIn && !accountCreated) {
        try {
          await publicApi.post('/customer-auth/register', {
            email: accountEmail || undefined,
            phone: accountPhone || undefined,
            password: accountPassword,
            firstName:
              customerFieldValues.first_name ?? estimate?.customer_first_name ?? undefined,
            lastName:
              customerFieldValues.last_name ?? estimate?.customer_last_name ?? undefined,
            // Claiming the record this document belongs to. WP-04's I-1 check
            // requires the portal token for exactly that, and this is the same
            // token that authorized the approval a moment ago.
            customerId: estimate?.customer_id ?? undefined,
            portalToken: token,
          });
          setAccountCreated(true);
          setAccountError(null);
          message.success('Your account is ready — sign in any time.', 4);
        } catch (regErr: any) {
          // Plain string by WP-03 contract, safe to render.
          setAccountError(
            regErr?.response?.data?.error ||
              'Your approval went through, but we could not create the account. You can set one up below.'
          );
        } finally {
          // Never keep the password around once it has been used or refused.
          setAccountPassword('');
          setAccountConfirm('');
        }
      }

      fetchEstimate(); // Reload to show approved status
    } catch (err: any) {
      console.error('Error approving estimate:', err);
      const body = err.response?.data;

      // WP-10 slice 3 — map the server's field-level rejection back onto the
      // inputs that caused it.
      //
      // WP-03 keeps `error` a plain string and puts structure in sibling keys,
      // so the prose still goes to the toast exactly as before while `missing`
      // / `fields` drive the per-input errors. Branching on `code`, which is the
      // stable contract; `error` is prose and may be reworded.
      //
      // CUSTOMER_INCOMPLETE also REPLACES the prompt's field list with the
      // server's current one. If staff filled in the address while the customer
      // had the page open, the list shrinks and the customer is not asked again
      // for something that is now on file.
      if (body?.code === 'CUSTOMER_INCOMPLETE' && Array.isArray(body.missing)) {
        setMissingFields(body.missing);
        setCaptureBlocked(true);
        setCustomerFieldErrors(
          body.missing.reduce((acc: Record<string, string>, m: MissingFieldSpec) => {
            acc[m.field] = 'Required before we can start the work';
            return acc;
          }, {})
        );
        setShowSignatureModal(false);
      } else if (body?.code === 'APPROVAL_REQUIREMENTS_UNMET' && Array.isArray(body.missing)) {
        // G1 refused the approval (Plan 2). The server is the authority even when
        // this page believed the record was complete — a policy change, a waiver
        // revoked, or staff clearing a value while the customer had the page
        // open all land here.
        //
        // MERGE, never replace. `missing` carries only entity/field/label, while
        // the list already on screen carries the prefilled values the customer
        // may have just corrected and the required/writable flags. Replacing it
        // would blank the form as a response to being told to fix the form.
        setMissingFields((prev) => {
          const known = new Set(prev.map((p) => p.field));
          const added: CaptureFieldSpec[] = body.missing
            .filter((m: MissingFieldSpec) => !known.has(m.field))
            .map((m: MissingFieldSpec) => ({ ...m, value: null, required: true }));
          return added.length > 0 ? [...prev, ...added] : prev;
        });
        // The server has just said this record cannot approve, so the gate goes
        // back on regardless of what the payload said when the page loaded.
        setCompletenessComplete(false);
        setCaptureBlocked(true);
        setCustomerFieldErrors(
          body.missing.reduce((acc: Record<string, string>, m: MissingFieldSpec) => {
            acc[m.field] = 'We need this before you can approve';
            return acc;
          }, {})
        );
        setShowSignatureModal(false);
        // The button they pressed is at the bottom of a long phone page and the
        // prompt is above it, so an unscrolled error is an invisible error.
        // Deferred a tick so the modal has closed and the prompt is laid out.
        // Guarded because jsdom does not implement scrollIntoView.
        setTimeout(() => {
          const el = capturePromptRef.current;
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 0);
      } else if (body?.code === 'FIELD_NOT_CAPTURABLE' && Array.isArray(body.fields)) {
        setCaptureBlocked(true);
        setCustomerFieldErrors(
          body.fields.reduce((acc: Record<string, string>, f: string) => {
            acc[f] = 'This one has to be updated by the shop';
            return acc;
          }, {})
        );
        setShowSignatureModal(false);
      }

      const errorMsg = body?.error || 'Failed to approve estimate. Please try again.';
      message.error(errorMsg, 5);
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    try {
      setProcessing(true);

      await publicApi.post(`/public/estimates/${token}/decline`, {
        reason: declineReason,
      });

      message.info('Estimate declined. Thank you for your feedback.', 3);
      setShowDeclineModal(false);
      fetchEstimate(); // Reload to show declined status
    } catch (err: any) {
      console.error('Error declining estimate:', err);
      const errorMsg = err.response?.data?.error || 'Failed to decline estimate. Please try again.';
      message.error(errorMsg, 5);
    } finally {
      setProcessing(false);
    }
  };

  // Appointment booking functions
  const loadBookingSettings = async () => {
    try {
      const response = await publicApi.get(`/public/appointments/settings`);
      setBookingSettings(response.data.booking);
      return response.data.booking;
    } catch (err: any) {
      console.error('Error loading booking settings:', err);
      return null;
    }
  };

  const checkCanSchedule = async () => {
    try {
      const response = await publicApi.get(`/public/estimates/${token}/can-schedule`);
      setCanSchedule(response.data);
      return response.data;
    } catch (err: any) {
      console.error('Error checking schedule capability:', err);
      return null;
    }
  };

  const loadAvailableSlots = async (date: Dayjs) => {
    if (!date || !estimate) return;

    setBookingLoading(true);
    try {
      // Calculate duration from estimate line items
      const totalHours = estimate.line_items.reduce((sum: number, item: any) => {
        return sum + (item.labor_hours || 0);
      }, 0);
      const durationMinutes = Math.round(totalHours * 60) || 60; // Default to 1 hour if no labor hours

      const startDate = date.format('YYYY-MM-DD');
      const endDate = date.format('YYYY-MM-DD');

      const response = await publicApi.get(`/public/appointments/availability`, {
        params: {
          start_date: `${startDate}T00:00:00`,
          end_date: `${endDate}T23:59:59`,
          duration_minutes: durationMinutes,
          appointment_type: 'repair'
        }
      });

      const availableOnly = response.data.filter((slot: any) => slot.available);

      // Deduplicate slots by time - since backend auto-assigns techs, customers only need to see unique times
      const uniqueSlots = availableOnly.reduce((acc: any[], slot: any) => {
        const isDuplicate = acc.some((s: any) =>
          s.start_time === slot.start_time && s.end_time === slot.end_time
        );
        if (!isDuplicate) {
          acc.push(slot);
        }
        return acc;
      }, []);

      console.log(`[BOOKING] Loaded ${response.data.length} total slots, ${availableOnly.length} available, ${uniqueSlots.length} unique time slots`);
      setAvailableSlots(uniqueSlots);
    } catch (err: any) {
      console.error('Error loading available slots:', err);
      message.error(err.response?.data?.error || 'Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setBookingLoading(false);
    }
  };

  // Load slots when date changes
  useEffect(() => {
    if (selectedDate && showBookingModal) {
      loadAvailableSlots(selectedDate);
    }
  }, [selectedDate, showBookingModal]);

  const handleBookAppointment = async () => {
    if (!selectedSlot) {
      message.error('Please select a time slot');
      return;
    }

    setBookingProcessing(true);
    try {
      await publicApi.post(`/public/estimates/${token}/book-appointment`, {
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: bookingNotes || undefined
        // Note: technician_id is NOT sent - backend will auto-assign best available technician
      });

      message.success('Appointment booked successfully! A technician will be assigned to your appointment.', 5);
      setShowBookingModal(false);
      setExistingAppointment({
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        technician_name: 'Assigned' // Will be assigned by system
      });
    } catch (err: any) {
      console.error('Error booking appointment:', err);
      const errorMessage = err.response?.data?.error || 'Failed to book appointment. Please try again.';
      message.error(errorMessage, 5);
    } finally {
      setBookingProcessing(false);
    }
  };

  const openBookingModal = async () => {
    const canSched = await checkCanSchedule();
    if (canSched?.can_schedule) {
      // Load booking settings
      const settings = await loadBookingSettings();
      if (settings) {
        setShowBookingModal(true);
        // Auto-select tomorrow as default date
        const tomorrow = dayjs().add(settings.booking_advance_days_min || 1, 'day');
        setSelectedDate(tomorrow);
      }
    } else {
      message.error(canSched?.reason || 'Cannot schedule this estimate');
    }
  };

  const disabledDate = (current: Dayjs) => {
    if (!bookingSettings) return true;

    // DatePicker passes candidates at 00:00, so compare against day boundaries
    // (start-of-day for min, end-of-day for max) to avoid greying out the
    // intended earliest/latest day due to the current clock time.
    const minDate = dayjs().add(bookingSettings.booking_advance_days_min, 'day').startOf('day');
    const maxDate = dayjs().add(bookingSettings.booking_advance_days_max, 'day').endOf('day');

    return current && (current < minDate || current > maxDate || current.day() === 0);
  };

  // WP-04: render the vehicle challenge BEFORE the loading and not-found
  // branches. Ordering matters: falling through to the 404 would tell a
  // customer holding a valid link that their estimate does not exist, and would
  // give anyone probing links the existence oracle the backend just closed.
  if (needsChallenge) {
    return (
      <VehicleChallengeGate
        documentToken={token as string}
        resource="estimates"
        onPassed={() => {
          setNeedsChallenge(false);
          setError(null);
          fetchEstimate();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Loading estimate...">
          <div style={{ padding: 50 }} />
        </Spin>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div style={{ padding: '40px' }}>
        <Result
          status="404"
          title="Estimate Not Found"
          subTitle={error || 'The estimate you are looking for does not exist or the link has expired.'}
          extra={
            <Button type="primary" onClick={() => navigate('/')}>
              Go Home
            </Button>
          }
        />
      </div>
    );
  }

  const handleLineItemStatusChange = (lineItemId: number, status: 'approved' | 'declined' | 'unselected') => {
    // Mark this line dirty so a concurrent live refetch preserves the choice.
    locallyChangedRef.current.add(lineItemId);
    setLineItemStatus(prev => ({
      ...prev,
      [lineItemId]: status,
    }));
  };

  const handleApproveAll = () => {
    if (!estimate) return;
    const allApproved: { [key: number]: 'approved' | 'declined' | 'unselected' } = {};
    estimate.line_items.forEach((item: any) => {
      allApproved[item.id] = 'approved';
      locallyChangedRef.current.add(item.id);
    });
    setLineItemStatus(allApproved);
    setShowSignatureModal(true);
  };

  const handleDeclineAll = () => {
    setShowDeclineModal(true);
  };

  const lineItemColumns = [
    {
      title: 'Select',
      key: 'status',
      width: 160,
      render: (record: any) => {
        const status = lineItemStatus[record.id];
        if (!isActionable) {
          return status === 'approved' ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>Approved</Tag>
          ) : status === 'declined' ? (
            <Tag color="error" icon={<CloseCircleOutlined />}>Declined</Tag>
          ) : (
            <Tag>Not Selected</Tag>
          );
        }
        return (
          <Space size="small">
            <Button
              type={status === 'approved' ? 'primary' : 'default'}
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleLineItemStatusChange(record.id, 'approved')}
              disabled={processing}
              style={{ background: status === 'approved' ? '#10b981' : undefined, borderColor: status === 'approved' ? '#10b981' : undefined }}
            >
              {status === 'approved' ? '✓' : 'Select'}
            </Button>
            <Button
              type={status === 'declined' ? 'primary' : 'default'}
              danger={status === 'declined'}
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => handleLineItemStatusChange(record.id, 'declined')}
              disabled={processing}
            >
              {status === 'declined' ? '✗' : 'Decline'}
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: '500' }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.category}</Text>
          {/*
            WP-10 slice 2 — the customer's note on this line (desktop).
            Same component as the mobile card path, so the two cannot drift.
          */}
          <CustomerLineNote
            lineId={record.id}
            initialNote={record.customer_note}
            editable={isActionable}
            onCommit={handleLineNoteCommit}
          />
        </div>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Labor',
      key: 'labor',
      width: 150,
      align: 'center' as const,
      render: (record: any) => {
        if (!record.labor_hours || !record.hourly_rate) return '-';
        const baseLaborCost = record.labor_hours * record.hourly_rate;
        const hasDiscount = record.labor_discount_percentage || record.labor_discount_amount;
        const laborDiscountAmount = record.labor_discount_amount ||
          (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;

        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px' }}>{record.labor_hours} hrs @ ${record.hourly_rate}/hr</div>
            {hasDiscount ? (
              <>
                <Text type="secondary" delete style={{ fontSize: '11px', display: 'block' }}>
                  ${baseLaborCost.toFixed(2)}
                </Text>
                <Text style={{ color: '#10b981', fontWeight: '500' }}>${discountedLaborCost.toFixed(2)}</Text>
                <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                  -{record.labor_discount_percentage
                    ? `${record.labor_discount_percentage}%`
                    : `$${laborDiscountAmount.toFixed(2)}`}
                </Text>
              </>
            ) : (
              <Text style={{ color: '#10b981', fontWeight: '500' }}>${baseLaborCost.toFixed(2)}</Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Parts',
      key: 'parts',
      width: 80,
      align: 'center' as const,
      render: (record: any) => {
        const partsCount = record.parts?.length || 0;
        return partsCount > 0 ? (
          <Tag color="blue">{partsCount} part{partsCount !== 1 ? 's' : ''}</Tag>
        ) : '-';
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 120,
      align: 'right' as const,
      render: (price: number, record: any) => {
        const status = lineItemStatus[record.id];
        const isDeclined = status === 'declined';
        const isUnselected = status === 'unselected';
        // Pre-discount line original = base labor + base parts (qty × unit), so
        // the Total column shows the same original→discounted chain the Labor and
        // Parts columns do, instead of only the discounted total.
        const baseLaborCost = (record.labor_hours || 0) * (record.hourly_rate || 0);
        const partsBase = (record.parts || []).reduce(
          (sum: number, p: any) => sum + (p.quantity || 0) * (p.unit_price || 0),
          0
        );
        const lineOriginal = baseLaborCost + partsBase;
        const hasLineDiscount = lineOriginal > price + 0.005;
        return (
          <div style={{ textAlign: 'right', opacity: isUnselected ? 0.5 : 1 }}>
            {hasLineDiscount && !isDeclined && (
              <Text type="secondary" delete style={{ fontSize: '11px', display: 'block' }}>
                ${lineOriginal.toFixed(2)}
              </Text>
            )}
            <Text
              strong
              style={{
                color: isDeclined || isUnselected ? '#999' : '#10b981',
                textDecoration: isDeclined ? 'line-through' : 'none'
              }}
            >
              ${price.toFixed(2)}
            </Text>
            {hasLineDiscount && !isDeclined && (
              <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                -${(lineOriginal - price).toFixed(2)}
              </Text>
            )}
          </div>
        );
      },
    },
  ];

  const partColumns = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'center' as const,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => `$${price.toFixed(2)}`,
    },
    {
      title: 'Total',
      key: 'total_price',
      width: 120,
      align: 'right' as const,
      render: (record: Part) => {
        const hasDiscount = record.discount_percentage || record.discount_amount;
        const basePrice = record.quantity * record.unit_price;

        if (hasDiscount && record.discounted_price !== undefined) {
          return (
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary" delete style={{ fontSize: '11px', display: 'block' }}>
                ${basePrice.toFixed(2)}
              </Text>
              <Text style={{ color: '#10b981', fontWeight: '500' }}>${record.discounted_price.toFixed(2)}</Text>
              <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>
                -{record.discount_percentage
                  ? `${record.discount_percentage}%`
                  : `$${(record.discount_amount || 0).toFixed(2)}`}
              </Text>
            </div>
          );
        }

        return <Text style={{ color: '#10b981' }}>${record.total_price.toFixed(2)}</Text>;
      },
    },
  ];

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: 'Draft' },
      sent: { color: 'processing', text: 'Sent' },
      approved: { color: 'success', text: 'Approved' },
      declined: { color: 'error', text: 'Declined' },
      converted: { color: 'purple', text: 'In Progress' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const isActionable = estimate.status === 'sent' || estimate.status === 'draft';

  // Mobile Card View for Line Items
  const renderMobileLineItems = () => {
    if (!estimate) return null;

    return estimate.line_items.map((item: any) => {
      const status = lineItemStatus[item.id];
      const isDeclined = status === 'declined';
      const isUnselected = status === 'unselected';
      const isApproved = status === 'approved';

      return (
        <Card
          key={item.id}
          size="small"
          style={{
            marginBottom: 12,
            borderColor: isApproved ? '#10b981' : isDeclined ? '#ef4444' : '#e5e7eb',
            borderWidth: 2,
            opacity: isUnselected ? 0.7 : 1,
          }}
        >
          {/* Status Buttons */}
          {isActionable && (
            <Space style={{ width: '100%', marginBottom: 12 }} size="small">
              <Button
                type={isApproved ? 'primary' : 'default'}
                size="small"
                block
                icon={<CheckCircleOutlined />}
                onClick={() => handleLineItemStatusChange(item.id, 'approved')}
                disabled={processing}
                style={{
                  flex: 1,
                  background: isApproved ? '#10b981' : undefined,
                  borderColor: isApproved ? '#10b981' : undefined
                }}
              >
                {isApproved ? '✓ Selected' : 'Select'}
              </Button>
              <Button
                type={isDeclined ? 'primary' : 'default'}
                danger={isDeclined}
                size="small"
                block
                icon={<CloseCircleOutlined />}
                onClick={() => handleLineItemStatusChange(item.id, 'declined')}
                disabled={processing}
                style={{ flex: 1 }}
              >
                {isDeclined ? '✗ Declined' : 'Decline'}
              </Button>
            </Space>
          )}

          {/* Item Details */}
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ display: 'block', fontSize: 16 }}>{item.description}</Text>
            <Tag color="blue" style={{ marginTop: 4 }}>{item.category}</Tag>
            {item.urgency && (
              <Tag color={item.urgency.color} style={{ marginTop: 4 }}>{item.urgency.label}</Tag>
            )}
          </div>

          {/* WP-10 slice 2 — the customer's note on this line (mobile). */}
          <CustomerLineNote
            lineId={item.id}
            initialNote={item.customer_note}
            editable={isActionable}
            onCommit={handleLineNoteCommit}
          />

          {/* Labor Info */}
          {item.labor_hours && item.hourly_rate && (
            <div style={{
              background: '#f9fafb',
              padding: 8,
              borderRadius: 6,
              marginBottom: 8
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Labor</Text>
              <div>
                <Text strong>{item.labor_hours} hrs @ ${item.hourly_rate}/hr</Text>
                {(() => {
                  const baseLaborCost = item.labor_hours * item.hourly_rate;
                  const hasDiscount = item.labor_discount_percentage || item.labor_discount_amount;
                  const laborDiscountAmount = item.labor_discount_amount ||
                    (baseLaborCost * (item.labor_discount_percentage || 0) / 100);
                  const discountedLaborCost = baseLaborCost - laborDiscountAmount;

                  return hasDiscount ? (
                    <div style={{ float: 'right', textAlign: 'right' }}>
                      <Text type="secondary" delete style={{ fontSize: 11 }}>
                        ${baseLaborCost.toFixed(2)}
                      </Text>
                      <br />
                      <Text style={{ color: '#10b981', fontWeight: '600' }}>
                        ${discountedLaborCost.toFixed(2)}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        -{item.labor_discount_percentage
                          ? `${item.labor_discount_percentage}%`
                          : `$${laborDiscountAmount.toFixed(2)}`}
                      </Text>
                    </div>
                  ) : (
                    <Text style={{ float: 'right', color: '#10b981' }}>
                      ${baseLaborCost.toFixed(2)}
                    </Text>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Parts */}
          {item.parts && item.parts.length > 0 && (
            <div style={{
              background: '#f9fafb',
              padding: 8,
              borderRadius: 6,
              marginBottom: 8
            }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Parts ({item.parts.length})
              </Text>
              {item.parts.map((part: any, idx: number) => {
                const hasDiscount = part.discount_percentage || part.discount_amount;
                const basePrice = part.quantity * part.unit_price;

                return (
                  <div key={part.id || idx} style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text style={{ fontSize: 13 }}>{part.part_number} - {part.description}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Qty {part.quantity} × ${part.unit_price.toFixed(2)}
                      </Text>
                      {hasDiscount && part.discounted_price !== undefined ? (
                        <div style={{ textAlign: 'right' }}>
                          <Text type="secondary" delete style={{ fontSize: 11 }}>
                            ${basePrice.toFixed(2)}
                          </Text>
                          <br />
                          <Text style={{ fontSize: 12, color: '#10b981', fontWeight: '600' }}>
                            ${part.discounted_price.toFixed(2)}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            -{part.discount_percentage
                              ? `${part.discount_percentage}%`
                              : `$${(part.discount_amount || 0).toFixed(2)}`}
                          </Text>
                        </div>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ${part.total_price.toFixed(2)}
                        </Text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quantity + line total (original → discounted, matching labor/parts) */}
          {(() => {
            const baseLaborCost = (item.labor_hours || 0) * (item.hourly_rate || 0);
            const partsBase = (item.parts || []).reduce(
              (sum: number, p: any) => sum + (p.quantity || 0) * (p.unit_price || 0),
              0
            );
            const lineOriginal = baseLaborCost + partsBase;
            const hasLineDiscount = lineOriginal > item.total_price + 0.005;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">Quantity: {item.quantity}</Text>
                <div style={{ textAlign: 'right', opacity: isUnselected ? 0.5 : 1 }}>
                  {hasLineDiscount && !isDeclined && (
                    <Text type="secondary" delete style={{ fontSize: 12, display: 'block' }}>
                      ${lineOriginal.toFixed(2)}
                    </Text>
                  )}
                  <Text
                    strong
                    style={{
                      fontSize: 18,
                      color: isDeclined || isUnselected ? '#999' : '#10b981',
                      textDecoration: isDeclined ? 'line-through' : 'none'
                    }}
                  >
                    ${item.total_price.toFixed(2)}
                  </Text>
                  {hasLineDiscount && !isDeclined && (
                    <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                      -${(lineOriginal - item.total_price).toFixed(2)}
                    </Text>
                  )}
                </div>
              </div>
            );
          })()}
        </Card>
      );
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, rgba(239, 246, 255, 0.2) 100%)',
      padding: isMobile ? '16px 12px' : '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/*
          Plan 3 Task 4 — this approval came back from the shop.

          At the very top, and OUTSIDE the `isActionable` block, because
          returning a submission does not revert the estimate's status: the
          estimate is already `approved`, so anything rendered inside the
          still-awaiting-a-decision block would never be seen by the one
          customer who needs to see it.

          Every returned field is listed with the staff note against it. The same
          notes are also passed to the capture prompt below (`fieldErrors`) so
          that, on an estimate that IS still actionable, the note sits against
          the input as well.
        */}
        {approvalReview?.state === 'returned' && (
          <div data-testid="approval-return-notice" style={{ marginBottom: 24 }}>
            <Alert
              type="warning"
              showIcon
              message="The shop needs a correction before this can go ahead"
              description={
                <div>
                  {approvalReview.return_note && (
                    <Paragraph style={{ marginBottom: 8 }}>{approvalReview.return_note}</Paragraph>
                  )}
                  {approvalReview.returned_fields.map((f) => (
                    <div
                      key={f.field}
                      data-testid={`returned-field-${f.field}`}
                      style={{ marginBottom: 4 }}
                    >
                      <Text strong style={{ fontSize: 13 }}>
                        {f.label}
                      </Text>
                      {f.note && (
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {' — '}
                          {f.note}
                        </Text>
                      )}
                    </div>
                  ))}
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    Reply to the shop or give us a call with the corrections and we&apos;ll get this
                    moving again.
                  </Text>
                </div>
              }
            />
          </div>
        )}

        {/* Booking CTA - Show prominently at top when approved */}
        {estimate.status === 'approved' && estimate.signed_at && !existingAppointment && (
          <Card
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #1890ff',
              marginBottom: 24
            }}
          >
            <Result
              status="success"
              title={
                <Title level={2} style={{ color: '#1890ff', margin: 0 }}>
                  🎉 Congratulations! Your Estimate is Approved!
                </Title>
              }
              subTitle={
                <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 600, margin: '20px auto' }}>
                  <Card style={{ background: 'white', textAlign: 'center' }}>
                    <Space direction="vertical" size="middle">
                      <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                      <Title level={4} style={{ margin: 0 }}>You're in the Right Hands!</Title>
                      <Paragraph style={{ fontSize: 16, margin: 0 }}>
                        Thank you for approving your estimate. Our expert technicians are ready to take care of your vehicle.
                      </Paragraph>
                      <Divider style={{ margin: '12px 0' }} />
                      <Alert
                        message="Next Step: Book Your Appointment"
                        description="Please select a convenient time below so we can schedule your service and get you back on the road!"
                        type="info"
                        showIcon
                        icon={<CalendarOutlined />}
                        style={{ textAlign: 'left' }}
                      />
                    </Space>
                  </Card>

                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<CalendarOutlined />}
                    onClick={openBookingModal}
                    style={{
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none',
                      height: 60,
                      fontSize: isMobile ? 16 : 18,
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
                      animation: 'pulse 2s infinite',
                    }}
                  >
                    {isMobile ? 'Book Appointment Now' : 'Book Your Appointment Now'}
                  </Button>
                  <style>{`
                    @keyframes pulse {
                      0%, 100% {
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
                        transform: scale(1);
                      }
                      50% {
                        box-shadow: 0 6px 20px rgba(24, 144, 255, 0.6);
                        transform: scale(1.02);
                      }
                    }
                  `}</style>

                  <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center' }}>
                    Approved on {new Date(estimate.signed_at).toLocaleString()}
                  </Text>
                </Space>
              }
            />
          </Card>
        )}

        {/* Header */}
        {!isInPerson && (<Card style={{ marginBottom: isMobile ? 16 : 24, borderRadius: 12 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>
                  Estimate #{estimate.estimate_number}
                </Title>
                <Space wrap>
                  {getStatusTag(estimate.status)}
                  {estimate.valid_until && (
                    <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
                      <CalendarOutlined /> Valid until {new Date(estimate.valid_until).toLocaleDateString()}
                    </Text>
                  )}
                </Space>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              {(() => {
                const hasSelections = Object.values(lineItemStatus).some(s => s === 'approved');
                // Single source of truth: derive the header total from the same
                // helper the canonical Totals block uses.
                const wholeJobDiscount = (estimate as any).discount_type && (estimate as any).discount_value != null
                  ? { type: (estimate as any).discount_type, value: Number((estimate as any).discount_value) }
                  : undefined;
                const approvedTotal = calculateEstimateTotals(
                  estimate.line_items,
                  estimate.tax_rate,
                  hasSelections ? lineItemStatus : undefined,
                  wholeJobDiscount,
                  deriveEstimateFees(estimate, wholeJobDiscount)
                ).total;

                return (
                  <div style={{ textAlign: 'right' }}>
                    {hasSelections ? (
                      <>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Selected Total:</Text>
                        <Title level={2} style={{ margin: 0, color: '#10b981' }}>
                          ${approvedTotal.toFixed(2)}
                        </Title>
                      </>
                    ) : (
                      <>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Full Estimate:</Text>
                        <Title level={2} style={{ margin: 0, color: '#999' }}>
                          ${estimate.total_amount.toFixed(2)}
                        </Title>
                      </>
                    )}
                  </div>
                );
              })()}
            </Col>
          </Row>
        </Card>
        )}

        {/* Customer & Vehicle Info */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12}>
            <Card title={<><UserOutlined /> Customer Information</>} variant="borderless">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>
                  {estimate.customer_company_name || `${estimate.customer_first_name} ${estimate.customer_last_name}`}
                </Text>
                <Text type="secondary">{estimate.customer_email}</Text>
                <Text type="secondary">{estimate.customer_phone}</Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title={<><CarOutlined /> Vehicle Information</>} variant="borderless">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong>{estimate.year} {estimate.make} {estimate.model}</Text>
                <Text type="secondary">VIN: {estimate.vin}</Text>
                <Text type="secondary">License: {estimate.license_plate}</Text>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Services */}
        <Card
          title={<><FileTextOutlined /> Services & Parts</>}
          style={{ marginBottom: 24 }}
        >
          {isActionable && (
            <Alert
              message="Review Services"
              description={isMobile
                ? "Tap Select or Decline on each service. Use buttons at bottom to approve all or sign selected."
                : "Review each service below. You can select individual services you want, or use the buttons at the bottom to approve all or decline all. The total updates in real-time as you make selections."
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Card View (≤768px) */}
          {isMobile ? (
            <div>{renderMobileLineItems()}</div>
          ) : (
            /* Table View (>768px) */
            <Table
              dataSource={estimate.line_items}
              columns={lineItemColumns}
              pagination={false}
              rowKey="id"
              size="small"
              expandedRowKeys={expandedRowKeys}
              onExpandedRowsChange={(keys) => setExpandedRowKeys(keys as number[])}
              expandable={{
                expandedRowRender: (record: any) => {
                  if (!record.parts || record.parts.length === 0) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                        No parts required for this service
                      </div>
                    );
                  }
                  return (
                    <div style={{ margin: 0 }}>
                      <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                        Parts Required:
                      </Text>
                      <Table
                        columns={partColumns}
                        dataSource={record.parts}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        showHeader={true}
                        style={{ marginLeft: '24px' }}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => record.parts && record.parts.length > 0,
              }}
            />
          )}

          {estimate.customer_notes && (
            <>
              <Divider />
              <div>
                <Text strong>Notes:</Text>
                <Paragraph style={{ marginTop: 8 }}>{estimate.customer_notes}</Paragraph>
              </div>
            </>
          )}

          <Divider />

          {/* Totals */}
          <Row justify={isMobile ? 'center' : 'end'}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <Space direction="vertical" size={8} style={{ width: '100%', fontSize: isMobile ? 14 : undefined }}>
                {(() => {
                  const hasApprovedItems = Object.values(lineItemStatus).some(s => s === 'approved');
                  const hasUnapprovedItems = Object.values(lineItemStatus).some(s => s !== 'approved');

                  // Whole-job (header) discount — applied after line discounts, before tax.
                  const wholeJobDiscount = (estimate as any).discount_type && (estimate as any).discount_value != null
                    ? { type: (estimate as any).discount_type, value: Number((estimate as any).discount_value) }
                    : undefined;
                  // Fees are back-derived from the stored fee-inclusive totals so the
                  // recomputed Tax/Total match the amount the customer is billed.
                  const fees = deriveEstimateFees(estimate, wholeJobDiscount);
                  // Use shared helper for correct pre-discount math
                  const totals = calculateEstimateTotals(
                    estimate.line_items,
                    estimate.tax_rate,
                    hasApprovedItems ? lineItemStatus : undefined,
                    wholeJobDiscount,
                    fees
                  );

                  return (
                    <>
                      {!hasApprovedItems && isActionable && (
                        <Row justify="space-between">
                          <Text type="secondary">Full Estimate Subtotal:</Text>
                          <Text type="secondary">${totals.preDiscountSubtotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      {hasApprovedItems && hasUnapprovedItems && (
                        <Row justify="space-between">
                          <Text type="secondary" style={{ fontSize: 12 }}>Full Estimate:</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            ${calculateEstimateTotals(estimate.line_items, estimate.tax_rate).preDiscountSubtotal.toFixed(2)}
                          </Text>
                        </Row>
                      )}
                      {hasApprovedItems && (
                        <Row justify="space-between">
                          <Text>{hasUnapprovedItems ? 'Selected Subtotal (before discounts):' : 'Subtotal (before discounts):'}</Text>
                          <Text>${totals.preDiscountSubtotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      {totals.totalDiscounts > 0 && (
                        <Row justify="space-between">
                          <Text style={{ color: '#ef4444' }}>Discounts:</Text>
                          <Text style={{ color: '#ef4444' }}>-${totals.totalDiscounts.toFixed(2)}</Text>
                        </Row>
                      )}
                      {totals.totalDiscounts > 0 && (
                        <Row justify="space-between">
                          <Text>Subtotal:</Text>
                          <Text>${totals.discountedSubtotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      {totals.jobDiscountAmount > 0 && (
                        <>
                          <Row justify="space-between">
                            <Text style={{ color: '#ef4444' }}>Job Discount:</Text>
                            <Text style={{ color: '#ef4444' }}>-${totals.jobDiscountAmount.toFixed(2)}</Text>
                          </Row>
                          <Row justify="space-between">
                            <Text>Discounted Subtotal:</Text>
                            <Text>${totals.taxableSubtotal.toFixed(2)}</Text>
                          </Row>
                        </>
                      )}
                      {totals.feesTotal > 0 && (
                        <Row justify="space-between">
                          <Text>Fees:</Text>
                          <Text>${totals.feesTotal.toFixed(2)}</Text>
                        </Row>
                      )}
                      <Row justify="space-between">
                        <Text>Tax ({estimate.tax_rate}%):</Text>
                        <Text>${totals.taxAmount.toFixed(2)}</Text>
                      </Row>
                      {/* Diagnosis already paid for. Shown as its own line, after
                          tax, because that is where it is applied — the customer
                          paid tax on the diagnosis when they paid for it, so it
                          reduces what is OWED, not the taxable amount. */}
                      {diagnosisCredit > 0 && (
                        <Row justify="space-between">
                          <Text style={{ color: '#10b981' }}>Diagnosis already paid:</Text>
                          <Text style={{ color: '#10b981' }} data-testid="diagnosis-credit-row">
                            −${diagnosisCredit.toFixed(2)}
                          </Text>
                        </Row>
                      )}
                      <Divider style={{ margin: '8px 0' }} />
                      <Row justify="space-between">
                        <Text strong style={{ fontSize: 16 }}>Total:</Text>
                        <Text strong style={{ fontSize: 16, color: hasApprovedItems ? '#10b981' : '#999' }}>
                          ${Math.max(0, round2(totals.total - diagnosisCredit)).toFixed(2)}
                        </Text>
                      </Row>
                      {hasApprovedItems && hasUnapprovedItems && (
                        <Row justify="space-between">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            (Based on selected services)
                          </Text>
                        </Row>
                      )}
                    </>
                  );
                })()}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* DVI Inspection Section */}
        {estimate.inspection && estimate.inspection.items?.length > 0 && (() => {
          // Filter to items worth showing to the customer: non-pending status,
          // or has photos, or has notes. Pure-pending with no evidence is clutter.
          const visibleItems = estimate.inspection!.items.filter(
            it =>
              it.status !== 'pending' ||
              it.photos.length > 0 ||
              (it.notes !== null && it.notes.trim() !== '') ||
              (!!it.reading_value && it.reading_value.trim() !== '')
          );
          if (visibleItems.length === 0) return null;

          // Group items by category_name, preserving insertion order
          const grouped: Record<string, DVIItem[]> = {};
          const catOrder: string[] = [];
          for (const it of visibleItems) {
            if (!grouped[it.category_name]) {
              grouped[it.category_name] = [];
              catOrder.push(it.category_name);
            }
            grouped[it.category_name].push(it);
          }

          const statusTag = (status: string) => {
            if (status === 'pass') return <Tag color="success">Pass</Tag>;
            if (status === 'attention') return <Tag color="warning">Attention</Tag>;
            if (status === 'fail') return <Tag color="error">Fail</Tag>;
            return null; // 'pending' → no tag shown
          };

          return (
            <Card
              title="Vehicle Inspection"
              style={{ marginBottom: 24 }}
            >
              {catOrder.map(cat => (
                <div key={cat} style={{ marginBottom: 16 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 15, color: '#374151' }}>
                    {cat}
                  </Text>
                  {grouped[cat].map(item => (
                    <div
                      key={item.id}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        marginBottom: 8,
                      }}
                    >
                      <Space wrap size="small" style={{ marginBottom: item.notes || item.photos.length > 0 ? 6 : 0 }}>
                        <Text style={{ fontWeight: 500 }}>{item.point_name}</Text>
                        {/*
                          WP-07: a measurement is no longer exclusive to
                          reading-type points — a preset can set one on any
                          point — so this renders whenever a value exists, and
                          prefixes the shop's configured label when there is one
                          ("Wear: 2mm"). The label is data; nothing here decides
                          what a measurement is called.
                        */}
                        {item.reading_value && item.reading_value.trim() !== '' && (
                          <Text strong data-testid={`reading-value-${item.id}`}>
                            {formatMeasurement(item.reading_value, item.unit, item.measurement_label)}
                          </Text>
                        )}
                        {statusTag(item.status)}
                      </Space>
                      {item.notes && (
                        <Text
                          type="secondary"
                          style={{ fontSize: 12, display: 'block', marginBottom: item.photos.length > 0 ? 6 : 0 }}
                        >
                          {item.notes}
                        </Text>
                      )}
                      {item.photos.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {item.photos.map((photo, idx) => (
                            <img
                              key={idx}
                              src={photo.file_url}
                              alt={item.point_name}
                              onClick={() => setPreviewPhoto(photo)}
                              style={{
                                width: 72,
                                height: 72,
                                objectFit: 'cover',
                                borderRadius: 6,
                                cursor: 'pointer',
                                border: '1px solid #d1d5db',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          );
        })()}

        {/* Photo Preview Modal */}
        <Modal
          open={!!previewPhoto}
          onCancel={() => setPreviewPhoto(null)}
          footer={null}
          centered
          width={isMobile ? '95%' : 700}
          styles={{ body: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 8 } }}
        >
          {previewPhoto && (
            <img
              src={previewPhoto.file_url}
              alt="Inspection photo"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </Modal>

        {/* Disclaimer */}
        {estimate.disclaimer && (
          <Card style={{ marginBottom: 24 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {estimate.disclaimer}
            </Text>
          </Card>
        )}

        {/* In-Person Signature Pad */}
        {isInPerson && isActionable && (
          <Card style={{ marginBottom: 24 }}>
            <Title level={4}>Customer Signature</Title>
            <div style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              backgroundColor: '#fafafa',
              touchAction: 'none',
              marginBottom: 12,
            }}>
              <SignatureCanvas
                ref={(ref: SignatureCanvas | null) => setInPersonSignatureRef(ref)}
                penColor="black"
                minWidth={1.5}
                canvasProps={{
                  width: 550,
                  height: 250,
                  style: { width: '100%', height: 250, touchAction: 'none' },
                }}
              />
            </div>
            <Space>
              <Button onClick={() => inPersonSignatureRef?.clear()}>
                Clear Signature
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                onClick={handleSignatureSubmit}
                loading={processing}
                disabled={approvalGated}
              >
                Sign &amp; Approve In Person
              </Button>
            </Space>
            {approvalGated && (
              <div style={{ marginTop: 8 }}>
                <Text
                  type="secondary"
                  data-testid="approve-blocked-reason-inperson"
                  style={{ fontSize: 12, display: 'block' }}
                >
                  {approveBlockedReason}
                </Text>
              </div>
            )}
          </Card>
        )}

        {/*
          WP-10 slice 4 — pay or put a deposit down, once the estimate is
          approved.

          Rendered OUTSIDE the `isActionable` block on purpose: that flag means
          "still awaiting a decision" (sent/draft), and payment only becomes
          relevant after the customer has approved. The panel decides for itself
          whether there is anything to collect — it renders nothing when the
          server says the document is not payable or the balance is settled.
        */}
        {token && (
          <EstimatePaymentPanel
            token={token}
            returningFromCheckout={searchParams.get('checkout') === 'return'}
            checkoutSessionId={searchParams.get('session_id')}
          />
        )}

        {/*
          WP-10 slice 5 — offer an account, once there is something to have an
          account about.

          Placed AFTER the payment panel deliberately. The customer came here to
          decide about work and, if they chose to, to pay for it; an account is
          the useful afterthought, not a gate in front of either. Nothing is
          rendered until the estimate is approved.
        */}
        {/*
          Still here for the customer who approved WITHOUT taking the offer above —
          including every estimate approved before that offer existed. Suppressed
          once an account was created during approval, so the same person is never
          asked twice.
        */}
        {token && !accountCreated && (
          <PostApprovalRegistration
            token={token}
            customerId={estimate.customer_id}
            approved={estimate.status === 'approved'}
            // Prefer what the customer JUST typed during approval, falling back
            // to the record the payload already carries. Without this the account
            // form opens blank and asks them to retype it: the banked-draft
            // prefill needs `stage_registration`, which compatibility mode (the
            // shipped default) does not grant, so the draft is always empty.
            prefill={{
              first_name:
                customerFieldValues.first_name ?? estimate.customer_first_name ?? null,
              last_name:
                customerFieldValues.last_name ?? estimate.customer_last_name ?? null,
              email: customerFieldValues.email ?? estimate.customer_email ?? null,
              phone: customerFieldValues.phone ?? estimate.customer_phone ?? null,
            }}
          />
        )}

        {/* Action Buttons */}
        {isActionable && (
          <Card>
            {/*
              WP-10 slice 3 — the details we still need, immediately above the
              approve buttons.

              Placed here rather than at the top of the page on purpose: it is a
              condition of approving, so it belongs next to the act of
              approving. A prompt at the top of a long phone page is scrolled
              past and forgotten by the time the customer reaches the button
              that it blocks.
            */}
            <div ref={capturePromptRef}>
            <MissingInfoPrompt
              missing={missingFields}
              values={customerFieldValues}
              // A returned approval's per-field notes render exactly where a
              // validation error would; a fresh refusal wins over standing
              // guidance, because it is the more recent fact about that field.
              fieldErrors={{ ...returnedFieldNotes, ...customerFieldErrors }}
              blocked={captureBlocked}
              onChange={(field, value) => {
                setCustomerFieldValues(prev => ({ ...prev, [field]: value }));
                // Clear this field's error as soon as the customer starts
                // fixing it; leaving it red while they type reads as "still
                // wrong" when they have not finished.
                setCustomerFieldErrors(prev => {
                  if (!prev[field]) return prev;
                  const next = { ...prev };
                  delete next[field];
                  return next;
                });
              }}
            />
            {/*
              The account offer, immediately under the details it reuses. Same
              placement reasoning as the prompt above: it belongs where the customer
              is already typing, not on a second screen after they have approved.
            */}
            {token && !accountCreated && (
              <ApprovalAccountSetup
                enabled={accountOptIn}
                onEnabledChange={(on) => {
                  setAccountOptIn(on);
                  setAccountError(null);
                }}
                password={accountPassword}
                onPasswordChange={(v) => {
                  setAccountPassword(v);
                  setAccountError(null);
                }}
                confirm={accountConfirm}
                onConfirmChange={(v) => {
                  setAccountConfirm(v);
                  setAccountError(null);
                }}
                identifier={accountIdentifier}
                error={accountError}
                disabled={processing}
              />
            )}
            </div>

            {/* Selection Summary */}
            {(() => {
              const approvedCount = Object.values(lineItemStatus).filter(s => s === 'approved').length;
              const declinedCount = Object.values(lineItemStatus).filter(s => s === 'declined').length;
              const totalCount = estimate.line_items.length;

              if (approvedCount > 0) {
                return (
                  <Alert
                    message={`${approvedCount} service${approvedCount !== 1 ? 's' : ''} selected${declinedCount > 0 ? `, ${declinedCount} declined` : ''} (${totalCount} total)`}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              } else {
                return (
                  <Alert
                    message="No services selected yet"
                    description="Select services above, or use the buttons below to approve all or decline all"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
            })()}

            {/*
              Why Approve is off, stated next to it. Rendered ABOVE the buttons
              so it is on screen at the moment the customer reaches for the one
              that will not respond — a reason below the fold is no reason at
              all. Declining is never gated: a customer who does not want the
              work must not have to hand over an address to say so.
            */}
            {approvalGated && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  <Text data-testid="approve-blocked-reason" style={{ fontSize: 13 }}>
                    {approveBlockedReason}
                  </Text>
                }
              />
            )}

            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                onClick={handleApproveAll}
                disabled={processing || approvalGated}
                loading={processing}
                style={{
                  // The green gradient is an inline style, so antd's disabled
                  // styling cannot override it — a gated button kept looking
                  // like a live primary one and invited the tap it refuses.
                  background: approvalGated
                    ? undefined
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: approvalGated ? undefined : 'none',
                  height: isMobile ? 50 : 56,
                }}
              >
                {processing ? 'Processing...' : 'Approve All & Sign'}
              </Button>
              {!isInPerson && (
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  disabled={
                    processing ||
                    approvalGated ||
                    !Object.values(lineItemStatus).some(s => s === 'approved')
                  }
                  loading={processing}
                  style={{ height: isMobile ? 50 : 56 }}
                >
                  {processing ? 'Processing...' : 'Sign Selected Services'}
                </Button>
              )}
              <Button
                danger
                size="large"
                block
                icon={<CloseCircleOutlined />}
                onClick={handleDeclineAll}
                disabled={processing}
                loading={processing}
                style={{ height: isMobile ? 50 : 56 }}
              >
                {processing ? 'Processing...' : 'Decline All'}
              </Button>
            </Space>
          </Card>
        )}

        {existingAppointment && (
          <Card>
            <Result
              status="success"
              title="Appointment Scheduled"
              subTitle={
                <Space direction="vertical" size="small">
                  <Text>Your appointment has been successfully booked!</Text>
                  <Text strong style={{ fontSize: 16 }}>
                    {dayjs(existingAppointment.start_time).format('MMMM D, YYYY [at] h:mm A')}
                  </Text>
                  <Alert
                    message="Technician Assigned"
                    description="A qualified technician has been automatically assigned to your appointment based on availability and expertise."
                    type="success"
                    showIcon
                    icon={<ToolOutlined />}
                    style={{ marginTop: 12 }}
                  />
                  <Text type="secondary">
                    Duration: {dayjs(existingAppointment.end_time).diff(dayjs(existingAppointment.start_time), 'minute')} minutes
                  </Text>
                </Space>
              }
              extra={
                <Text type="secondary">
                  We'll send you a confirmation email shortly with all the details. See you then!
                </Text>
              }
            />
          </Card>
        )}

        {estimate.status === 'declined' && (
          <Card>
            <Result
              status="warning"
              title="Estimate Declined"
              subTitle="This estimate has been declined. The shop will contact you to discuss alternatives."
            />
          </Card>
        )}
      </div>

      {/* Signature Modal */}
      <Modal
        title="Sign Estimate"
        open={showSignatureModal}
        onCancel={() => setShowSignatureModal(false)}
        onOk={handleSignatureSubmit}
        okText={processing ? 'Submitting...' : 'Approve & Submit'}
        confirmLoading={processing}
        okButtonProps={{ disabled: processing }}
        cancelButtonProps={{ disabled: processing }}
        width={isMobile ? '95%' : 600}
        centered
        closable={!processing}
        maskClosable={!processing}
      >
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: isMobile ? 13 : 14 }}>
            Please sign below to approve this estimate:
          </Text>
          <div style={{
            border: '2px dashed #d9d9d9',
            borderRadius: 8,
            backgroundColor: '#fafafa',
            touchAction: 'none'
          }}>
            <SignatureCanvas
              ref={(ref: SignatureCanvas | null) => setSignatureRef(ref)}
              penColor="black"
              minWidth={1.5}
              canvasProps={{
                width: 550,
                height: isMobile ? 200 : 200,
                style: { width: '100%', height: isMobile ? 200 : 200, touchAction: 'none' },
              }}
            />
          </div>
          <Button
            size={isMobile ? 'middle' : 'small'}
            onClick={() => signatureRef?.clear()}
            style={{ marginTop: 12, width: isMobile ? '100%' : 'auto' }}
          >
            Clear Signature
          </Button>
        </div>
      </Modal>

      {/* Decline Modal */}
      <Modal
        title="Decline Estimate"
        open={showDeclineModal}
        onCancel={() => setShowDeclineModal(false)}
        onOk={handleDecline}
        okText={processing ? 'Declining...' : 'Decline'}
        okButtonProps={{ danger: true, disabled: processing }}
        cancelButtonProps={{ disabled: processing }}
        confirmLoading={processing}
        width={isMobile ? '95%' : 520}
        centered
        closable={!processing}
        maskClosable={!processing}
      >
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: isMobile ? 13 : 14 }}>
            Please let us know why you're declining this estimate (optional):
          </Text>
          <TextArea
            rows={isMobile ? 3 : 4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter reason for declining..."
            style={{ fontSize: isMobile ? 14 : 16 }}
          />
        </div>
      </Modal>

      {/* Booking Modal */}
      <Modal
        title="Book Your Appointment"
        open={showBookingModal}
        onCancel={() => {
          setShowBookingModal(false);
          setSelectedDate(null);
          setSelectedSlot(null);
          setAvailableSlots([]);
        }}
        footer={null}
        width={isMobile ? '95%' : 1000}
        centered
        styles={{
          body: {
            padding: isMobile ? '16px' : '24px'
          }
        }}
      >
        <div>
          {/* Quick Find Next Available */}
          {!selectedDate && bookingSettings && (
            <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
              <Row align="middle" gutter={16}>
                <Col flex="auto">
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ color: 'white', fontSize: 16 }}>
                      <ThunderboltOutlined /> Find Next Available Appointment
                    </Text>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 14 }}>
                      Quickly book the soonest available time slot
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Button
                    size="large"
                    onClick={() => {
                      const nextDate = dayjs().add(bookingSettings.booking_advance_days_min, 'day');
                      setSelectedDate(nextDate);
                    }}
                    style={{ height: 50 }}
                  >
                    Find Slots
                  </Button>
                </Col>
              </Row>
            </Card>
          )}

          <Row gutter={[24, 24]}>
            {/* Date Picker Column */}
            <Col xs={24} lg={10}>
              <Card title="Select a Date" extra={<CalendarOutlined />}>
                <DatePicker
                  style={{ width: '100%', marginBottom: 16 }}
                  value={selectedDate}
                  onChange={setSelectedDate}
                  disabledDate={disabledDate}
                  size="large"
                  format="MMMM D, YYYY"
                />
                {bookingSettings && (
                  <Alert
                    message="Booking Window"
                    description={`You can book appointments ${bookingSettings.booking_advance_days_min} to ${bookingSettings.booking_advance_days_max} days in advance. Sundays are closed.`}
                    type="info"
                    showIcon
                    icon={<ClockCircleOutlined />}
                  />
                )}
              </Card>
            </Col>

            {/* Time Slots Column */}
            <Col xs={24} lg={14}>
              <Card
                title={selectedDate ? `Available Times - ${selectedDate.format('MMMM D, YYYY')}` : 'Select a date to see available times'}
                extra={selectedDate && <Tag color="blue">{availableSlots.length} slots</Tag>}
              >
                {!selectedDate ? (
                  <Empty
                    description="Please select a date from the calendar"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                  />
                ) : bookingLoading ? (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin size="large">
                      <div style={{ paddingTop: 20 }}>Loading available time slots...</div>
                    </Spin>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <Empty
                    description="No available time slots for this date"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                  >
                    <Button
                      type="primary"
                      onClick={() => {
                        const nextDay = selectedDate.add(1, 'day');
                        if (!disabledDate(nextDay)) {
                          setSelectedDate(nextDay);
                        }
                      }}
                    >
                      Try Next Day
                    </Button>
                  </Empty>
                ) : (
                  <div>
                    {/* Next Available Highlight */}
                    {availableSlots.length > 0 && (
                      <Card
                        style={{
                          marginBottom: 16,
                          background: '#f6ffed',
                          borderColor: '#52c41a',
                          cursor: 'pointer'
                        }}
                        onClick={() => setSelectedSlot(availableSlots[0])}
                        hoverable
                      >
                        <Row align="middle" gutter={16}>
                          <Col>
                            <ThunderboltOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                          </Col>
                          <Col flex="auto">
                            <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                              Next Available
                            </Text>
                            <br />
                            <Text strong style={{ fontSize: 18 }}>
                              {dayjs(availableSlots[0].start_time).format('h:mm A')} - {dayjs(availableSlots[0].end_time).format('h:mm A')}
                            </Text>
                          </Col>
                          <Col>
                            {selectedSlot === availableSlots[0] && (
                              <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                            )}
                          </Col>
                        </Row>
                      </Card>
                    )}

                    <Divider orientation="left">All Available Times</Divider>

                    {/* Group slots by time of day */}
                    {(() => {
                      const morning = availableSlots.filter(slot => {
                        const hour = dayjs(slot.start_time).hour();
                        return hour >= 6 && hour < 12;
                      });
                      const afternoon = availableSlots.filter(slot => {
                        const hour = dayjs(slot.start_time).hour();
                        return hour >= 12 && hour < 17;
                      });
                      const evening = availableSlots.filter(slot => {
                        const hour = dayjs(slot.start_time).hour();
                        return hour >= 17 && hour < 22;
                      });

                      return (
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                          {morning.length > 0 && (
                            <div>
                              <Text type="secondary" strong>
                                <ClockCircleOutlined /> Morning (6 AM - 12 PM)
                              </Text>
                              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                {morning.map((slot, index) => (
                                  <Col xs={12} sm={8} key={index}>
                                    <Button
                                      block
                                      size="large"
                                      type={selectedSlot === slot ? 'primary' : 'default'}
                                      onClick={() => setSelectedSlot(slot)}
                                      icon={selectedSlot === slot && <CheckCircleOutlined />}
                                    >
                                      {dayjs(slot.start_time).format('h:mm A')}
                                    </Button>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}

                          {afternoon.length > 0 && (
                            <div>
                              <Text type="secondary" strong>
                                <ClockCircleOutlined /> Afternoon (12 PM - 5 PM)
                              </Text>
                              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                {afternoon.map((slot, index) => (
                                  <Col xs={12} sm={8} key={index}>
                                    <Button
                                      block
                                      size="large"
                                      type={selectedSlot === slot ? 'primary' : 'default'}
                                      onClick={() => setSelectedSlot(slot)}
                                      icon={selectedSlot === slot && <CheckCircleOutlined />}
                                    >
                                      {dayjs(slot.start_time).format('h:mm A')}
                                    </Button>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}

                          {evening.length > 0 && (
                            <div>
                              <Text type="secondary" strong>
                                <ClockCircleOutlined /> Evening (5 PM - 10 PM)
                              </Text>
                              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                {evening.map((slot, index) => (
                                  <Col xs={12} sm={8} key={index}>
                                    <Button
                                      block
                                      size="large"
                                      type={selectedSlot === slot ? 'primary' : 'default'}
                                      onClick={() => setSelectedSlot(slot)}
                                      icon={selectedSlot === slot && <CheckCircleOutlined />}
                                    >
                                      {dayjs(slot.start_time).format('h:mm A')}
                                    </Button>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}
                        </Space>
                      );
                    })()}
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Selected Time Summary */}
          {selectedSlot && (
            <Alert
              message="Time Slot Selected"
              description={
                <Space>
                  <CalendarOutlined />
                  <Text strong>
                    {selectedDate?.format('MMMM D, YYYY')} at {dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}
                  </Text>
                </Space>
              }
              type="success"
              showIcon
              style={{ marginTop: 24 }}
            />
          )}

          {/* Additional Notes */}
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Additional Notes (Optional)
            </Text>
            <TextArea
              rows={3}
              value={bookingNotes}
              onChange={(e) => setBookingNotes(e.target.value)}
              placeholder="Any special requests or information we should know?"
              maxLength={500}
            />
          </div>

          {/* Action Buttons */}
          <Divider />
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button
                size="large"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedDate(null);
                  setSelectedSlot(null);
                  setAvailableSlots([]);
                }}
                block
              >
                Cancel
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                size="large"
                onClick={handleBookAppointment}
                loading={bookingProcessing}
                disabled={!selectedSlot || bookingProcessing}
                block
                icon={<CheckCircleOutlined />}
              >
                {bookingProcessing ? 'Booking...' : 'Confirm Appointment'}
              </Button>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default PublicEstimateView;
